import { useEffect, useState } from 'react';

/**
 * Waits until the given image sources and the web fonts are loaded,
 * so the page can be revealed in one single, clean fade instead of
 * popping in piece by piece. Falls back after `timeout` ms.
 */
export const useAssetsReady = (sources: string[], timeout = 4000) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadImage = (src: string) =>
      new Promise<void>((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
        if (img.complete) resolve();
      });

    const fonts =
      typeof document !== 'undefined' && 'fonts' in document
        ? (document as Document & { fonts: FontFaceSet }).fonts.ready.then(() => undefined)
        : Promise.resolve();

    const done = () => {
      if (!cancelled) setReady(true);
    };

    Promise.all([fonts, ...sources.map(loadImage)]).then(done);
    const timer = window.setTimeout(done, timeout);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources.join('|'), timeout]);

  return ready;
};
